import React, { useEffect } from 'react';
import {
    DefaultFlexStrechedWrapper,
    MoreItemsLink,
    NamesList,
    PanelSectionContent,
    PanelSubHeader,
} from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { StatusIcon } from 'src/components/ui/status-icon/CargoConsumablesIcon.styles';
import {
    ReportOptionCheckbox,
    ReportOptionDescriptionText,
    ReportOptionIconWrapper,
    ReportOptionWrapper,
} from 'src/components/page-header/stowage/PageHeaderStowage.style';
import Tooltip from 'src/components/ui/tooltip/Tooltip';
import styled from 'styled-components';
import { getComparison } from 'src/components/cargo-temp-log/CargoTemperatureLogUtils';

const TankCountBadge = styled(StatusIcon)`
    background-color: ${({ theme }) => theme.colors.background.lightGrey};
    color: ${({ theme }) => theme.colors.text.heading};
    border-radius: 12px;
    padding: 2px 8px;
    font-size: 12px;
    margin-left: 8px;
    position: relative;
    height: auto;
    width: auto;
    right: auto;
`;

interface ReportPanelTankSelectionProps {
    selectedTanks: Tank[];
    filterOnSelectedTanks: boolean;
    setFilterOnSelectedTanks: (filter: boolean) => void;
    chartererFilterActive: boolean;
}

const MAX_VISIBLE_TANKS = 5;

const ReportPanelTankSelection: React.FC<ReportPanelTankSelectionProps> = ({
    selectedTanks,
    filterOnSelectedTanks,
    setFilterOnSelectedTanks,
    chartererFilterActive,
}) => {
    const _isSelectedTanksDisabled = selectedTanks.length === 0;

    useEffect(() => {
        // When chartererFilterActive becomes true, this sets the underlying state for tank selection to effectively "All tanks" (if charterer filter were false).
        // The actual visual representation (both checkboxes appearing unchecked) is handled by the `checked` prop logic using `!chartererFilterActive`.
        // When chartererFilterActive becomes false, this ensures "All tanks" is selected.
        setFilterOnSelectedTanks(false);
    }, [chartererFilterActive, setFilterOnSelectedTanks]);

    const handleAllTanksSelect = () => {
        setFilterOnSelectedTanks(false);
    };

    const handleSelectedTanksSelect = () => {
        if (!_isSelectedTanksDisabled) {
            setFilterOnSelectedTanks(true);
        }
    };

    // Sort tanks
    const _sortedSelectedTanks = selectedTanks.sort((a, b) => getComparison(true, '', '', a.tankName, b.tankName));

    // Get visible tanks and remaining tanks
    const _visibleTanks = _sortedSelectedTanks.slice(0, MAX_VISIBLE_TANKS);
    const _remainingTanks = _sortedSelectedTanks.slice(MAX_VISIBLE_TANKS);
    const _hasMoreTanks = _remainingTanks.length > 0;

    const renderRemainingTanksTooltip = () => {
        const tankNames = _remainingTanks.map((_tank) => _tank.tankName).join(', ');
        return <div>{tankNames}</div>;
    };

    return (
        <PanelSectionContent>
            <PanelSubHeader noBorder>Tanks</PanelSubHeader>

            {/* All tanks option */}
            <Tooltip
                text="Tank filtering is unavailable while a Charterer filter is active. Clear the Charterer selection to filter by tanks instead."
                type="information"
                disabled={!chartererFilterActive}
            >
                <ReportOptionWrapper onClick={!chartererFilterActive ? handleAllTanksSelect : undefined} disabled={chartererFilterActive}>
                    <DefaultFlexStrechedWrapper noMargin>
                        <ReportOptionIconWrapper>
                            <ReportOptionCheckbox
                                type="checkbox"
                                checked={!filterOnSelectedTanks && !chartererFilterActive}
                                onChange={handleAllTanksSelect}
                                onClick={(e) => e.stopPropagation()}
                                disabled={chartererFilterActive}
                            />
                        </ReportOptionIconWrapper>
                        <ReportOptionDescriptionText>All tanks</ReportOptionDescriptionText>
                    </DefaultFlexStrechedWrapper>
                </ReportOptionWrapper>
            </Tooltip>

            {/* Selected tanks option */}
            <Tooltip
                text={
                    chartererFilterActive
                        ? 'Tank filtering is unavailable while a Charterer filter is active. Clear the Charterer selection to filter by tanks instead.'
                        : 'No tanks are currently selected in the stowage plan. Select one or more tanks first to use this filter.'
                }
                type="information"
                disabled={!(_isSelectedTanksDisabled || chartererFilterActive)}
            >
                <ReportOptionWrapper
                    onClick={!(_isSelectedTanksDisabled || chartererFilterActive) ? handleSelectedTanksSelect : undefined}
                    disabled={_isSelectedTanksDisabled || chartererFilterActive}
                >
                    <DefaultFlexStrechedWrapper noMargin>
                        <ReportOptionIconWrapper>
                            <ReportOptionCheckbox
                                type="checkbox"
                                checked={filterOnSelectedTanks && !_isSelectedTanksDisabled && !chartererFilterActive}
                                onChange={handleSelectedTanksSelect}
                                onClick={(e) => e.stopPropagation()}
                                disabled={_isSelectedTanksDisabled || chartererFilterActive}
                            />
                        </ReportOptionIconWrapper>
                        <ReportOptionDescriptionText>
                            Selected tanks
                            {selectedTanks.length > 0 && <TankCountBadge>{selectedTanks.length}</TankCountBadge>}
                        </ReportOptionDescriptionText>
                    </DefaultFlexStrechedWrapper>

                    {/* Render tank names if selected and not empty */}
                    {filterOnSelectedTanks && selectedTanks.length > 0 && (
                        <NamesList>
                            {_visibleTanks.map((_tank) => (
                                <div key={_tank.tankId}>
                                    {_tank.tankName}
                                    {_visibleTanks.indexOf(_tank) < _visibleTanks.length - 1 || _hasMoreTanks ? ',' : ''}
                                </div>
                            ))}

                            {_hasMoreTanks && (
                                <Tooltip text={renderRemainingTanksTooltip()} type="information">
                                    <MoreItemsLink>and {_remainingTanks.length} more...</MoreItemsLink>
                                </Tooltip>
                            )}
                        </NamesList>
                    )}
                </ReportOptionWrapper>
            </Tooltip>
        </PanelSectionContent>
    );
};

export default ReportPanelTankSelection;
